import React, { useEffect } from 'react';
import Head from 'next/head';
import {
  Placeholder,
  LayoutServiceData,
  Field,
  ImageField,
  getFieldValue,
  Item,
  ComponentRendering,
  HTMLLink,
} from '@sitecore-jss/sitecore-jss-nextjs';
import ScriptTags from './components/helpers/ScriptTags/ScriptTags';
import { useI18n } from 'next-localization';
import { useRouter } from 'next/router';
import { HP_LayoutServiceContext } from 'models/layout';
import CoveoSimpleSearch from './components/Coveo/CoveoSimpleSearch/CoveoSimpleSearch';
import config from 'temp/config';
import { pageHasComponent } from 'lib/utils';
import { Components } from 'lib/enum';

interface LayoutProps {
  layoutData: LayoutServiceData;
  appUrl?: string;
  headLinks: HTMLLink[];
}

interface RouteFields {
  [key: string]: unknown;
  componentName: string;
  Title?: Field;
}

const Layout = ({ layoutData, appUrl, headLinks }: LayoutProps): React.JSX.Element => {
  const { events } = useRouter();
  const { t } = useI18n();

  const publicUrl = appUrl || config.publicUrl;
  const { route } = layoutData.sitecore;
  const context = layoutData.sitecore.context as HP_LayoutServiceContext;
  const fields = route?.fields as RouteFields;
  const isPageEditing = layoutData.sitecore.context.pageEditing;
  const mainClassPageEditing = isPageEditing ? 'editing-mode blue-green' : 'prod-mode blue-green';

  const pageHasFormComponent = pageHasComponent(route?.placeholders, Components.JssNextForm);

  useEffect(() => {
    events.on('routeChangeComplete', (_url) => {
      document.body.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    });
  }, [events]);

  //removes specified string from a given url
  const removeLang = (url: string, removeThis: string) => {
    if (!url) return '';
    let updateURL = url.replace('/' + removeThis + '/', '/');
    if (updateURL.endsWith('/' + removeThis)) {
      updateURL = updateURL.substring(0, updateURL.length - (removeThis.length + 1));
    }
    if (updateURL.startsWith(removeThis + '/')) {
      updateURL = updateURL.substring(removeThis.length + 1);
    }
    return updateURL;
  };

  useEffect(() => {
    const links: NodeListOf<HTMLAnchorElement> = document.querySelectorAll('a'); //find all link tags in page
    const matchLinks = Array.from(links).filter((link) => {
      return (
        link.href.toLocaleLowerCase().indexOf('upmchealthplan.') >= 0 &&
        link.href.toLocaleLowerCase().indexOf('en') >= 0
      );
    }); //restrict to links that are local and contain the default language code 'en'

    matchLinks.forEach((link) => {
      const updated = removeLang(link.href, 'en');
      link.href = updated;
    });
  });

  const filteredURL = removeLang(context.pageUrl, 'en'); //remove default language code from URLs appearing in header

  const pageSuffix: string = getFieldValue<boolean>(fields, 'appendSiteSuffix')
    ? ' ' + t('MetaData-MetaTitleSuffix').trim()
    : '';

  const pageTitle: string =
    (getFieldValue(fields, 'title') || getFieldValue(fields, 'pageMetaTitle') || 'Page') + pageSuffix;
  const pageMetaTitle: string =
    (getFieldValue(fields, 'pageMetaTitle') || getFieldValue(fields, 'title') || 'Page') + pageSuffix;

  const pageDesc: string = getFieldValue(fields, 'pageMetaDescription') ?? '';

  const ogImg = fields.openGraphImage as ImageField;
  const RawHtml = ({ html = '' }): React.JSX.Element =>
    isPageEditing ? <></> : <script dangerouslySetInnerHTML={{ __html: `</script>${html}<script>` }} />;

  // ROBOTS
  const robotsDirectives = (fields.robotsDirectiveValue as Array<Item>) || [];
  const robots = robotsDirectives.length ? robotsDirectives.map((r) => r.name) : [];
  const robotText = robots.join(', ');

  const templateName = route?.templateName?.replace(' ', '-').toLowerCase();

  const MainBottomPlaceholder = (): React.JSX.Element | null => {
    if (!isPageEditing && route?.placeholders['hp-main-bottom']?.length === 0) return null;

    return (
      <div id="main-bottom" className="main-bottom">
        {route && <Placeholder name="hp-main-bottom" rendering={route} disableSuspense={true} />}
      </div>
    );
  };

  const BreadcrumbPlaceholder = (): React.JSX.Element | null => {
    if (
      route?.placeholders['hp-breadcrumbs'].find((y) => (y as ComponentRendering).componentName == 'Breadcrumbs') ==
      undefined
    )
      return null;

    return <>{route && <Placeholder name="hp-breadcrumbs" rendering={route} disableSuspense={true} />}</>;
  };

  return (
    <>
      <ScriptTags beforeInteractiveFlag={true} scripts={context.thirdPartyHeadScripts} />
      <Head>
        <title>{pageTitle || fields?.Title?.value?.toString() || 'Page'}</title>
        {pageHasFormComponent && <meta name="has-form-component" content="true" />}

        <link rel="icon" href={`/favicon.ico`} />
        {headLinks.map((headLink) => (
          <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
        ))}

        {/* CookieBot Script */}
        <RawHtml html={context.cookieBot} />

        {/* SEO metadata */}
        <meta name="title" content={pageMetaTitle} />
        <meta name="description" content={pageDesc} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {getFieldValue(fields, 'pageMetaKeywords') ? (
          <meta name="keywords" content={getFieldValue(fields, 'pageMetaKeywords')} />
        ) : null}

        {/* OpenGraph metadata */}
        <meta
          name="og:title"
          content={getFieldValue(fields, 'openGraphTitle') ? getFieldValue(fields, 'openGraphTitle') : pageTitle}
        />
        <meta
          name="og:description"
          content={
            getFieldValue(fields, 'openGraphDescription') ? getFieldValue(fields, 'openGraphDescription') : pageDesc
          }
        />
        {getFieldValue(fields, 'openGraphURL') ? (
          <meta name="og:url" content={getFieldValue(fields, 'openGraphURL')} />
        ) : publicUrl.length ? (
          <meta name="og:url" content={publicUrl + filteredURL} />
        ) : null}
        {ogImg?.value?.src && publicUrl.length ? (
          <meta name="og:image" content={`${publicUrl}/${ogImg.value.src.toString()}`} />
        ) : null}
        {getFieldValue(fields, 'openGraphType') ? (
          <meta name="og:type" content={getFieldValue(fields, 'openGraphType')} />
        ) : null}
        {getFieldValue(fields, 'openGraphSiteName') ? (
          <meta name="og:site_name" content={getFieldValue(fields, 'openGraphSiteName')} />
        ) : null}
        {getFieldValue(fields, 'openGraphLocale') ? (
          <meta name="og:locale" content={getFieldValue(fields, 'openGraphLocale')} />
        ) : null}
        {getFieldValue(fields, 'canonicalUrl') ? (
          <link rel="canonical" href={getFieldValue(fields, 'canonicalUrl')} />
        ) : publicUrl.length ? (
          <link rel="canonical" href={publicUrl + filteredURL} />
        ) : null}
        {robotText !== '' ? <meta name="robots" content={robotText} /> : null}
      </Head>

      {/* root placeholder for the app, which we add components to using route data */}
      <div className={mainClassPageEditing}>
        <div id="header-container">
          {route && <Placeholder name="hp-header" rendering={route} disableSuspense={true} />}
        </div>
        <main id="main" className={templateName}>
          <div id="main-container" className="container-md">
            <div id="main-inner" className="main-inner">
              {route && <Placeholder name="hp-banner" rendering={route} disableSuspense={true} />}
              <BreadcrumbPlaceholder />
              {route && <Placeholder name="hp-main" rendering={route} disableSuspense={true} />}
            </div>
            <MainBottomPlaceholder />
          </div>
        </main>
        <footer id="footer-container">
          {route && <Placeholder name="hp-footer" rendering={route} disableSuspense={true} />}
        </footer>
      </div>
      {getFieldValue(fields, 'enableScript') ? <CoveoSimpleSearch /> : null}
      <ScriptTags beforeInteractiveFlag={false} scripts={context.thirdPartyBodyBottomScripts} />
    </>
  );
};

export default Layout;
